import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

import { ProviderProductCodeModel } from '@models/types'

import type { EntitiesState } from '@redux/entities'
import type { EntityOptions } from '@redux/modules/types'

const watchEntityKeys = ['providerProductCodes']

const sortByServiceProductTitle = (
  providerProductCodes: EntitiesState['providerProductCodes'],
  serviceProducts: EntitiesState['serviceProducts'],
) => {
  // @ts-expect-error TS2349
  const sorted = providerProductCodes.sort((a, b) => {
    if (serviceProducts[a.service_product_id].title < serviceProducts[b.service_product_id].title){
      return -1
    }

    if (serviceProducts[a.service_product_id].title > serviceProducts[b.service_product_id].title){
      return 1
    }

    return 0
  })

  return sorted
}

type ProviderProductCodeFilters = {
  provider_id?: number,
  service_id?: number,
  service_product_id?: number,
  string?: string,
}

interface UseEntitiesOptions {
  entityKey?: string,
  filters?: ProviderProductCodeFilters,
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

function useProviderProductCodes(options: UseEntitiesOptions = {}) {
  const { entityKey, filters = {} } = options
  const {
    provider_id: filterProviderId,
    service_product_id: filterServiceProductId,
    service_id: filterServiceId,
    string: filterString,
  } = filters || {}

  const {
    updatedEntities: { providerProductCodes: providerProductCodesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const {
    providerProductCodes,
    serviceProducts,
  } = useSelector(reduxState => reduxState.entities)

  const filteredProviderProductCodes = useMemo((): ProviderProductCodeModel[] => {
    const filtered = Object.values(providerProductCodes).filter((providerProductCode) => {
      const {
        product_code,
        provider_id,
        service_product_id,
      } = providerProductCode

      const serviceProduct = serviceProducts[service_product_id] || {}
      const { service_id } = serviceProduct

      const matchProductCode = matchFilterString(product_code, filterString)
      const matchService = matchFilterNumber(service_id, filterServiceId)
      const matchProviderId = matchFilterNumber(provider_id, filterProviderId)
      const matchServiceProduct = matchFilterNumber(service_product_id, filterServiceProductId)

      return matchProviderId && matchServiceProduct && matchService && matchProductCode
    })

    // @ts-expect-error TS2345
    return sortByServiceProductTitle(filtered, serviceProducts)
  }, [providerProductCodesUpdatedAt, JSON.stringify(filters)])

  const filteredProviderProductCodesCount = filteredProviderProductCodes.length
  const hasFilteredProviderProductCodes = !!filteredProviderProductCodesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredProviderProductCodesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingProviderProductCodes } = useReduxAction(
    'providerProductCodes',
    'loadProviderProductCodes',
    {
      entityKey,
      ...defaultRequestOptions.providerProductCode,
      ...filtersWithOffset,
      limit,
    },
    [filters, performHttpRequests],
    {
      // @ts-expect-error TS2339
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: { loadMore },
    canLoadMore,
    filteredProviderProductCodes,
    filteredProviderProductCodesCount,
    hasFilteredProviderProductCodes,
    loading: loadingProviderProductCodes,
  }
}

export default useProviderProductCodes
